import React, { useMemo, useState } from 'react';
import { AgendaItem } from '../types';
import AgendaDay from './AgendaDay';
import Alert from './ErrorDisplay';
import Modal from './Modal';

interface AgendaPageProps {
  agendaItems: AgendaItem[];
  activityGroups?: Array<{
    id: number;
    agendaItemId?: number | null;
    agendaItem?: {
      id: number;
    } | null;
  }>;
  myArbeitsgruppeId?: number | null;
  myActivityGroupIds?: number[];
  onArbeitsgruppeClick?: (id: number) => void;
  onActivityGroupClick?: (id: number) => void;
}

// Simple sanitizer to remove script tags and on* attributes
const sanitizeHTML = (html: string) => {
  if (typeof DOMParser === 'undefined' || !html) {
    return '';
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');

  doc.querySelectorAll('script, style').forEach(el => el.remove());

  doc.querySelectorAll('*').forEach(el => {
    for (const attr of Array.from(el.attributes)) {
      if (attr.name.startsWith('on')) {
        el.removeAttribute(attr.name);
      }
    }
  });

  return doc.body.innerHTML;
};

const AgendaPage: React.FC<AgendaPageProps> = ({ agendaItems, activityGroups, myArbeitsgruppeId, myActivityGroupIds, onArbeitsgruppeClick, onActivityGroupClick }) => {
  const [selectedItem, setSelectedItem] = useState<AgendaItem | null>(null);
  const [activeTab, setActiveTab] = useState<number>(0);

  const groupedAgenda = useMemo(() => {
    if (!agendaItems) return {};
    const visibleItems = agendaItems.filter(item => {
      return !item.hide;
    });
    const sortedData = [...visibleItems].sort((a, b) => {
      const dateCompare = new Date(a.date).getTime() - new Date(b.date).getTime();
      if (dateCompare !== 0) return dateCompare;
      return a.order - b.order;
    });
    return sortedData.reduce((acc, item) => {
      const date = new Date(item.date).toISOString().split('T')[0]; // ISO format: YYYY-MM-DD
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(item);
      return acc;
    }, {} as Record<string, AgendaItem[]>);
  }, [agendaItems]);

  const registeredAgendaItemIds = useMemo(() => {
    if (!activityGroups || !myActivityGroupIds) return new Set<number>();
    return new Set(
      activityGroups
        .filter(group => myActivityGroupIds.includes(group.id))
        .map(group => group.agendaItemId)
        .filter((id): id is number => typeof id === 'number')
    );
  }, [activityGroups, myActivityGroupIds]);

  const days = useMemo(() => Object.keys(groupedAgenda), [groupedAgenda]);

  const formatDate = (isoDate: string) => {
    return new Date(isoDate).toLocaleDateString('de-DE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTabDate = (isoDate: string) => {
    const date = new Date(isoDate);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleDateString('de-DE', { month: 'short' });
    return `${day}. ${month.charAt(0).toUpperCase() + month.slice(1)}`;
  };

  const handleItemClick = (item: AgendaItem) => {
    // Handle activity_groups type items
    if (item.type === 'activity_groups' && onActivityGroupClick) {
      onActivityGroupClick(0); // Pass 0 as ID, which will navigate to activity_groups list
      return;
    }

    if (activityGroups && myActivityGroupIds != null && onActivityGroupClick) {
      const matchedGroup = activityGroups.find(group => {
        const agendaItemId = group.agendaItemId ?? group.agendaItem?.id ?? null;
        return Number(agendaItemId) === Number(item.id) && myActivityGroupIds.includes(group.id);
      });
      if (matchedGroup) {
        onActivityGroupClick(matchedGroup.id);
        return;
      }
    }
    // Handle arbeitsgruppen type items
    if (item.type === 'arbeitsgruppen' && onArbeitsgruppeClick) {
      if (myArbeitsgruppeId) {
        // User is registered, navigate to the detail page
        onArbeitsgruppeClick(myArbeitsgruppeId);
      } else {
        // User is not registered, navigate to the list page by passing null or special value
        // The navigation handler will interpret this and go to the list
        onArbeitsgruppeClick(0); // Using 0 as a signal to navigate to list
      }
      return;
    }
    if (item.content) {
      setSelectedItem(item);
    }
  };

  const handleCloseModal = () => {
    setSelectedItem(null);
  };

  if (!agendaItems || agendaItems.length === 0) {
    return <Alert title="Keine Agenda" message="Für diese Veranstaltung sind keine Agendapunkte verfügbar." type="info" />;
  }

  const currentDay = days[activeTab];
  const currentItems = groupedAgenda[currentDay];

  return (
    <>
      {/* Combined Box: Tabs + Content */}
      <div className="bg-white rounded-lg shadow-lg border-gray-200 border-1 overflow-hidden">
        {/* Tabs */}
        <div className="overflow-x-auto border-b border-gray-200 mb-4">
          <div className="flex min-w-max">
            {days.map((day, index) => (
              <button
                key={day}
                onClick={() => setActiveTab(index)}
                className={`flex-1 px-4 py-3 text-sm md:text-base font-semibold whitespace-nowrap transition-colors ${activeTab === index
                    ? 'text-primary border-b-2 border-primary bg-primary/5'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
              >
                {formatTabDate(day)}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          <AgendaDay
            key={currentDay}
            date={formatDate(currentDay)}
            items={currentItems}
            onItemClick={handleItemClick}
            registeredAgendaItemIds={registeredAgendaItemIds}
            myArbeitsgruppeId={myArbeitsgruppeId}
          />
        </div>
      </div>

      {selectedItem && (
        <Modal
          isOpen={!!selectedItem}
          onClose={handleCloseModal}
          title={selectedItem.title}
          titleHtml={sanitizeHTML(selectedItem.title)}
        >
          <div
            className="prose prose-sm md:prose-base max-w-none prose-headings:text-gray-800 prose-p:text-gray-600 prose-a:text-pink-600 prose-strong:text-gray-700 prose-img:rounded-lg"
            dangerouslySetInnerHTML={{ __html: sanitizeHTML(selectedItem.content || '') }}
          />
        </Modal>
      )}
    </>
  );
};

export default AgendaPage;
